import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, RefreshCcw } from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { UrgentCaseModal } from './receptionist/modals/UrgentCaseModal';
import { WaitlistModal } from './receptionist/modals/WaitlistModal';
import { AppointmentList } from './receptionist/components/AppointmentList';
import { CounselorSchedule } from './receptionist/components/CounselorSchedule';
import { DashboardStats } from './receptionist/components/DashboardStats';
import { QuickActions } from './receptionist/components/QuickActions';
import { useDashboardStats } from '../../hooks/useDashboardStats';
import { SERVICE_TYPES } from './receptionist/constants';

export const ReceptionistDashboard = () => {
  const navigate = useNavigate();
  const { post } = useApi();
  const { stats, loading, error, refetch } = useDashboardStats();
  const [showUrgentModal, setShowUrgentModal] = useState(false);
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleUrgentCase = async (data) => {
    try {
      const response = await post('/appointments/urgent', {
        clientId: data.clientId,
        serviceType: SERVICE_TYPES.URGENT_COUNSELING,
        isUrgent: true,
        urgencyReason: data.urgencyReason,
      });

      if (response.data?.status === 'success') {
        setShowUrgentModal(false);
        refetch();
      }
    } catch (error) {
      console.error('Failed to create urgent case:', error);
    }
  };

  const handleCheckIn = async (appointmentId) => {
    try {
      const response = await post(`/appointments/check-in/${appointmentId}`);
      if (response.data?.status === 'success') {
        refetch();
      }
    } catch (error) {
      console.error('Failed to check in:', error);
    }
  };

  const handleScheduleFromWaitlist = async (waitlistId, scheduleData) => {
    try {
      const response = await post(`/appointments/waitlist/${waitlistId}/schedule`, scheduleData);
      if (response.data?.status === 'success') {
        setShowWaitlistModal(false);
        refetch();
      }
    } catch (error) {
      console.error('Failed to schedule from waitlist:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <RefreshCcw className="h-8 w-8 animate-spin text-uzima-maroon-500" />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Reception Dashboard</h1>
        <p className="text-gray-500">Manage appointments and counselor schedules</p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Overview */}
      <DashboardStats stats={stats} />

      {/* Quick Actions */}
      <QuickActions
        onNewAppointment={() => navigate('/receptionist/appointments/new')}
        onUrgentCase={() => setShowUrgentModal(true)}
        onViewWaitlist={() => setShowWaitlistModal(true)}
      />

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Appointments Section */}
        <div className="lg:col-span-2">
          <AppointmentList
            appointments={stats.upcomingArrivals}
            onCheckIn={handleCheckIn}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
          />
        </div>

        {/* Counselor Schedule Section */}
        <div>
          <CounselorSchedule />
        </div>
      </div>

      {/* Modals */}
      <UrgentCaseModal
        isOpen={showUrgentModal}
        onClose={() => setShowUrgentModal(false)}
        onSubmit={handleUrgentCase}
      />

      <WaitlistModal
        isOpen={showWaitlistModal}
        onClose={() => setShowWaitlistModal(false)}
        waitlist={stats.waitlist}
        onSchedule={handleScheduleFromWaitlist}
      />
    </div>
  );
};

export default ReceptionistDashboard;